import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiRecipientsIndex, type Recipient } from "../lib/api";

function useFlashMessage() {
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const flash = params.get("flash");
  if (flash === "created") {
    return "Recipient created successfully.";
  }
  if (flash === "updated") {
    return "Recipient updated successfully.";
  }
  if (flash === "deleted") {
    return "Recipient deleted successfully.";
  }
  return null;
}

export default function RecipientsIndex() {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const flash = useFlashMessage();

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const items = await apiRecipientsIndex();
        if (mounted) {
          setRecipients(items ?? []);
        }
      } catch (err: any) {
        console.error("Failed to load recipients", err);
        if (mounted) {
          setError("Unable to load recipients. Please try again.");
          setRecipients([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="page py-6">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Flash / error messages */}
        {flash && <div className="alert-success">{flash}</div>}
        {error && <div className="alert-danger">{error}</div>}

        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="title-page">Recipients</h1>
            <p className="text-sm text-gray-500">
              Saved beneficiaries you can use when creating transfers.
            </p>
          </div>
          <div className="flex-shrink-0">
            <Link to="/recipients/create" className="btn">
              Add New Recipient
            </Link>
          </div>
        </div>

        {/* Table card */}
        <div className="section-box overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm">
              <thead>
                <tr className="table-header">
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Currency</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-gray-500">
                      <div className="skeleton h-4 w-40 mx-auto mb-2" />
                      <div className="skeleton h-4 w-32 mx-auto" />
                    </td>
                  </tr>
                )}

                {!loading && recipients.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-6 text-center text-gray-500 text-sm"
                    >
                      You haven&apos;t added any recipients yet.
                    </td>
                  </tr>
                )}

                {!loading &&
                  recipients.length > 0 &&
                  recipients.map((r) => {
                    const primaryAccount = r.accounts?.[0];
                    const currency = primaryAccount?.currency ?? r.currency ?? "-";

                    return (
                      <tr key={r.id} className="table-row">
                        <td className="py-2 px-3">
                          <div className="font-medium text-gray-900">
                            {r.name}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-gray-700">
                          {currency}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex justify-end gap-2">
                            {/* View */}
                            <Link
                              to={`/recipients/${r.id}`}
                              className="btn btn-white px-2 py-1"
                              title="View recipient"
                            >
                              <span className="sr-only">View</span>
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  d="M1.5 12s3.75-6.75 10.5-6.75S22.5 12 22.5 12 18.75 18.75 12 18.75 1.5 12 1.5 12Z"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </Link>

                            {/* Edit */}
                            <Link
                              to={`/recipients/${r.id}/edit`}
                              className="btn btn-white px-2 py-1"
                              title="Edit recipient"
                            >
                              <span className="sr-only">Edit</span>
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  d="M15.232 5.232l3.536 3.536M9 11l6-6 3 3-6 6H9v-3z"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M3 21h18"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </Link>

                            {/* Pay (go straight to transfer details step) */}
                            <Link
                              to={`/transfers/create/details?recipient_id=${r.id}`}
                              className="btn btn-white px-2 py-1"
                              title="Make a transfer to this recipient"
                            >
                              <span className="sr-only">Pay</span>
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  d="M4 17l8-8 4 4 4-8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M4 21h16"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
